import { Box } from "@mui/material";
import { useMemo } from "react";
import { ReactElement } from "react";

import { GetActivePricingBundlesQuery } from "~frontend/__generated__/graphql";
import Table from "~frontend/components/Table";
import { TableBox } from "~frontend/components/Table";
import TagChip from "~frontend/components/TagChip";
import { compareSkuNames } from "~frontend/routes/organization/pricing/helpers";
import { formatMillicentsAsDollars } from "~frontend/routes/organization/pricingV1/priceFormatting";

type Bundle = GetActivePricingBundlesQuery["getActivePricingBundles"][number];
type Sku = Bundle["prices"][number]["sku"];

export default function PricingBundlesTable({
  bundles,
  page,
  pageSize,
  onChangePage,
  onChangePageSize,
}: {
  bundles: Bundle[];
  page: number;
  pageSize: number;
  onChangePage: (page: number) => void;
  onChangePageSize: (pageSize: number) => void;
}): ReactElement {
  const skus = useMemo(() => {
    const skuMap = new Map<string, Sku>();
    for (const bundle of bundles) {
      for (const price of bundle.prices) {
        if (!skuMap.has(price.sku.id)) {
          skuMap.set(price.sku.id, price.sku);
        }
      }
    }
    return [...skuMap.values()].sort((a, b) => compareSkuNames(a.name, b.name));
  }, [bundles]);

  const rows = bundles.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <TableBox>
      <Table
        columns={[
          {
            key: "name",
            title: "Pricing Option",
            renderValue: bundle => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {bundle.name}
                {bundle.isWeText && <TagChip label="WeText" />}
              </Box>
            ),
          },
          ...skus.map(sku => ({
            key: sku.id,
            title: sku.name,
            renderValue: (bundle: Bundle) => {
              const price = bundle.prices.find(p => p.sku.id === sku.id);
              return price
                ? `$${formatMillicentsAsDollars(price.priceInMillicents)}`
                : "—";
            },
          })),
        ]}
        rows={rows}
        totalCount={bundles.length}
        rowsPerPage={pageSize}
        onChangeRowsPerPage={newSize => {
          onChangePageSize(newSize);
          onChangePage(0);
        }}
        page={page}
        onChangePage={onChangePage}
        emptyState="No pricing bundles found"
      />
    </TableBox>
  );
}
